import * as Location from 'expo-location';
import { Platform } from 'react-native';

export interface GeocodeResult {
	latitude: number;
	longitude: number;
	accuracy?: number;
}

export interface AddressParts {
	address?: string;
	city?: string;
	state?: string;
	zipCode?: string;
}

/**
 * Build a full address string from address parts
 */
export function buildAddressString(parts: AddressParts): string {
	const components = [
		parts.address,
		parts.city,
		parts.state,
		parts.zipCode,
	].filter(Boolean);

	return components.join(', ');
}

/**
 * Geocode an address string to latitude/longitude coordinates
 * Uses expo-location's free geocoding service (Apple Maps on iOS, Google on Android)
 *
 * @param address - Full address string to geocode
 * @returns Promise<GeocodeResult | null> - Coordinates or null if geocoding fails
 */
export async function geocodeAddress(address: string): Promise<GeocodeResult | null> {
	if (!address || address.trim().length === 0) {
		return null;
	}

	// Skip geocoding on web platform (expo-location geocoding not available)
	if (Platform.OS === 'web') {
		console.log('Geocoding skipped on web platform');
		return null;
	}

	try {
		const results = await Location.geocodeAsync(address.trim());

		if (!results || results.length === 0) {
			console.warn('No geocoding results found for address:', address);
			return null;
		}

		// Use the first (most relevant) result
		const result = results[0];

		if (!result.latitude || !result.longitude) {
			console.warn('Invalid coordinates in geocoding result:', result);
			return null;
		}

		return {
			latitude: result.latitude,
			longitude: result.longitude,
			accuracy: result.accuracy,
		};
	} catch (error) {
		console.error('Geocoding failed for address:', address, error);
		return null;
	}
}

/**
 * Geocode address parts to coordinates
 * Combines address parts into a single string and geocodes it
 *
 * @param parts - Address components
 * @returns Promise<GeocodeResult | null> - Coordinates or null if geocoding fails
 */
export async function geocodeAddressParts(parts: AddressParts): Promise<GeocodeResult | null> {
	const address = buildAddressString(parts);
	if (!address) {
		return null;
	}

	return geocodeAddress(address);
}

/**
 * Check if coordinates are valid
 */
export function isValidCoordinates(lat?: number | null, lng?: number | null): boolean {
	if (lat === null || lat === undefined || lng === null || lng === undefined) {
		return false;
	}

	return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/**
 * Format coordinates for display
 */
export function formatCoordinates(lat: number, lng: number, precision: number = 5): string {
	return `${lat.toFixed(precision)}, ${lng.toFixed(precision)}`;
}

/**
 * Get platform-specific maps URL for opening coordinates in external maps app
 */
export function getMapsUrl(latitude: number, longitude: number, title?: string): string {
	const coords = `${latitude},${longitude}`;
	const label = title ? encodeURIComponent(title) : '';

	switch (Platform.OS) {
		case 'ios':
			// Apple Maps on iOS
			return label ? `maps://?q=${label}&ll=${coords}` : `maps://?ll=${coords}`;
		case 'android':
			// Google Maps on Android
			return label ? `geo:${coords}?q=${coords}(${label})` : `geo:${coords}`;
		case 'web':
		default:
			// Google Maps web
			return label ? `https://www.google.com/maps?q=${coords}(${label})` : `https://www.google.com/maps?q=${coords}`;
	}
}

